import { useEffect, useMemo, useState } from "react";
import { Player } from "@remotion/player";
import { apiFetch, apiUrl } from "@/api/client";
import { useDynamicComponent } from "@/hooks/useDynamicComponent";
import { resolveDurationFrames } from "@/lib/durationResolve";
import type { FieldDefinition, Template } from "@/types/template";

interface CardHoverPlayerProps {
  template: Template;
}

/**
 * Stripped-down Remotion Player for the hover-preview on TemplateCard.
 *
 * Mounts only when the card is hovered. Compiles the template's TSX on
 * first hover via /api/preview/compile, then plays the composition with
 * the field-schema defaults as inputProps. Loops while hovered. Unmounts
 * on mouseleave so the static thumbnail underneath becomes visible again.
 */
export function CardHoverPlayer({ template }: CardHoverPlayerProps) {
  const [moduleUrl, setModuleUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Build the default props from the schema. The owner can edit these on
  // the detail page, but the listing always plays the canonical defaults.
  const inputProps = useMemo(() => {
    const out: Record<string, unknown> = {};
    let schema: FieldDefinition[] = [];
    if (Array.isArray(template.field_schema)) {
      schema = template.field_schema;
    } else if (typeof template.field_schema === "string") {
      try {
        schema = JSON.parse(template.field_schema) as FieldDefinition[];
      } catch {
        schema = [];
      }
    }
    for (const f of schema) {
      out[f.key] = f.default;
    }
    return out;
  }, [template.field_schema]);

  // Compile on mount. The card unmounts this whole component on
  // mouseleave, so this fires per hover-session — but the backend's
  // /preview/compile is content-hashed, so a second hover just gets a
  // cached module URL with no recompile cost.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await apiFetch<{ module_url: string }>(
          "/preview/compile",
          {
            method: "POST",
            body: JSON.stringify({ tsx_code: template.tsx_code }),
          },
        );
        if (cancelled) return;
        setModuleUrl(apiUrl(result.module_url));
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "compile failed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [template.tsx_code]);

  const Component = useDynamicComponent(moduleUrl);

  const fps =
    typeof inputProps.fps === "number" && inputProps.fps > 0
      ? (inputProps.fps as number)
      : template.fps || 30;
  const durationFrames = resolveDurationFrames(
    inputProps,
    fps,
    template.duration_frames,
    template.field_schema,
  );

  if (error || !Component) {
    // Loading or compile error — render nothing so the underlying static
    // thumbnail (sibling under TemplateCard) remains visible.
    return null;
  }

  // Ensure component is non-null before passing to Player (TS narrowing).
  return (
    <Player
      component={Component}
      inputProps={{
        ...inputProps,
        // Same content-scale convention RemotionPreview passes to the
        // Player so templates that read these don't crash on undefined.
        __contentScale: 1,
        __baseWidth: template.width,
        __baseHeight: template.height,
        __compWidth: template.width,
        __compHeight: template.height,
      }}
      durationInFrames={durationFrames}
      fps={fps}
      compositionWidth={template.width}
      compositionHeight={template.height}
      style={{ width: "100%", height: "100%" }}
      autoPlay
      loop
      controls={false}
      acknowledgeRemotionLicense
    />
  );
}
